/**
 * Filter Count Thunk
 *
 * Fetches filter counts for hotel search.
 * Called during hotel search to populate filter options with counts.
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import { getOrCreateSessionId } from '@/shared/utils';
import { getUUID } from '@/shared/utils/appCookies';
import type { FilterCategory } from '@contracts';
import { fetchHotelFilterCount } from '../api/filterCountApi';

// ============================================
// Types
// ============================================

export interface FetchFilterCountParams {
  readonly city: string;
  /** Force refetch even if data exists */
  readonly forceRefetch?: boolean;
}

// ============================================
// Thunk
// ============================================

export const fetchFilterCountThunk = createAsyncThunk<
  FilterCategory[],
  FetchFilterCountParams,
  { state: RootState; rejectValue: string }
>(
  'hotels/filterCount/fetch',
  async (params, { rejectWithValue }) => {
    const sessionId = getOrCreateSessionId();
    const response = await fetchHotelFilterCount({
      sessionId,
      userId: getUUID(),
      city: params.city,
    });

    if (!response.success || response.filters === null) {
      return rejectWithValue(response.error?.message ?? 'Failed to fetch filter counts');
    }

    return response.filters;
  },
  {
    // condition runs BEFORE pending action - proper way to skip
    condition: (params, { getState }) => {
      const { data, error, isLoading } = getState().hotels.filterCount;

      // Force refetch: proceed unless already loading
      if (params.forceRefetch) {
        return !isLoading;
      }

      // Skip if already loading, data exists, or there was an error (prevent retry loop)
      // Error state should only retry via forceRefetch (manual retry button)
      return !(isLoading || data !== null || error !== null);
    },
  },
);
